/**
 * Aircraft Configuration Service
 * Provides aircraft axle configurations and load data for Airport ESAL calculations
 *
 * Data source: ../data/aircraft-configurations.json (centralized configuration)
 * Excel source: TRAFFIC LOAD FAA.txt aircraft definitions
 *
 * Used by:
 * - ESALTypesAircraft.ts (Aircraft configuration display and ESAL calculations)
 *
 * Purpose: Eliminates hardcoded aircraft configurations, centralizes 10 aircraft models with
 * complete axle configuration, load distribution, and Excel traceability
 */

import aircraftData from '../data/aircraft-configurations.json';

export interface AircraftConfiguration {
    id: number;
    name: string;
    fullName: string;
    axles: number;
    steeringAxles: number;
    singleAxles: number;
    tandemAxles: number;
    tridemAxles: number;
    steeringAxleLoad: number;
    singleAxleLoad: number;
    tandemAxleLoad: number;
    tridemAxleLoad: number;
    steeringLoadPercentage: number;
    singleLoadPercentage: number;
    tandemLoadPercentage: number;
    tridemLoadPercentage: number;
    designTotalLoad: number;
    direction1Cell: string;
    direction2Cell: string;
    imagePath: string;
    excelReferences: {
        axleConfig: string;
        loadPercentages: string;
        axleLoads: string;
        totalLoad: string;
    };
}

export interface AxleBreakdown {
    steering: number;
    single: number;
    tandem: number;
    tridem: number;
}

export interface AxleLoads {
    steering: number;
    single: number;
    tandem: number;
    tridem: number;
}

export interface LoadPercentages {
    steering: number;
    single: number;
    tandem: number;
    tridem: number;
}

/**
 * Aircraft configuration lookup map
 * Loaded from centralized JSON configuration (../data/aircraft-configurations.json)
 * Values based on Excel TRAFFIC LOAD FAA.txt definitions
 */
const AIRCRAFT_CONFIGURATIONS: Map<number, AircraftConfiguration> = new Map(
    aircraftData.aircraftConfigurations.map((config: AircraftConfiguration) => [config.id, config])
);

export class AircraftConfigurationService {
    /**
     * Get complete aircraft configuration
     *
     * @param aircraftId - Aircraft identifier (1-10)
     * @returns Complete aircraft configuration or undefined
     *
     * @example
     * ```typescript
     * const config = AircraftConfigurationService.getConfiguration(1);
     * console.log(config?.name); // "BOMBARDIER"
     * console.log(config?.designTotalLoad); // 454
     * ```
     */
    static getConfiguration(aircraftId: number): AircraftConfiguration | undefined {
        return AIRCRAFT_CONFIGURATIONS.get(aircraftId);
    }

    /**
     * Get total axle count for an aircraft
     *
     * Aircraft axle counts:
     * - BOMBARDIER (ID=1) → 3 axles
     * - ATR 42 (ID=2) → 3 axles
     * - AIRBUS A330 (ID=3) → 5 axles
     * - BOEING 737-300 (ID=4) → 3 axles
     * - BOEING 767-400 ER (ID=5) → 5 axles
     * - BOEING 777-300 ER (ID=6) → 7 axles
     * - BOEING B747 JUMBO (ID=7) → 9 axles
     * - Lockheed C-5 Galaxy (ID=8) → 13 axles
     * - AIRBUS A 380 (ID=9) → 11 axles
     * - Antonov An-225 Mriya (ID=10) → 14 axles
     *
     * @param aircraftId - Aircraft identifier (1-10)
     * @returns Total number of axles
     *
     * @example
     * ```typescript
     * const axles = AircraftConfigurationService.getAxleCount(7); // Returns 9 (B747)
     * ```
     */
    static getAxleCount(aircraftId: number): number {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        return config?.axles ?? 0;
    }

    /**
     * Get axle breakdown by type (steering, single, tandem, tridem)
     *
     * @param aircraftId - Aircraft identifier
     * @returns Breakdown of axle counts by type
     *
     * @example
     * ```typescript
     * const breakdown = AircraftConfigurationService.getAxleBreakdown(3);
     * // Returns: { steering: 1, single: 2, tandem: 2, tridem: 0 } for AIRBUS A330
     * ```
     */
    static getAxleBreakdown(aircraftId: number): AxleBreakdown | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        if (!config) return undefined;

        return {
            steering: config.steeringAxles,
            single: config.singleAxles,
            tandem: config.tandemAxles,
            tridem: config.tridemAxles,
        };
    }

    /**
     * Get axle loads in kilonewtons (kN) for each axle type
     *
     * @param aircraftId - Aircraft identifier
     * @returns Load values for each axle type in kN
     *
     * @example
     * ```typescript
     * const loads = AircraftConfigurationService.getAxleLoads(1);
     * // Returns: { steering: 45.4, single: 204.3, tandem: 0, tridem: 0 } for BOMBARDIER
     * ```
     */
    static getAxleLoads(aircraftId: number): AxleLoads | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        if (!config) return undefined;

        return {
            steering: config.steeringAxleLoad,
            single: config.singleAxleLoad,
            tandem: config.tandemAxleLoad,
            tridem: config.tridemAxleLoad,
        };
    }

    /**
     * Get load distribution percentages for each axle type
     * Percentages sum to 100%
     *
     * @param aircraftId - Aircraft identifier
     * @returns Percentage breakdown for each axle type
     *
     * @example
     * ```typescript
     * const percentages = AircraftConfigurationService.getLoadPercentages(3);
     * // Returns: { steering: 10, single: 45, tandem: 45, tridem: 0 } for AIRBUS A330
     * // Sum = 100%
     * ```
     */
    static getLoadPercentages(aircraftId: number): LoadPercentages | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        if (!config) return undefined;

        return {
            steering: config.steeringLoadPercentage,
            single: config.singleLoadPercentage,
            tandem: config.tandemLoadPercentage,
            tridem: config.tridemLoadPercentage,
        };
    }

    /**
     * Get design total load for aircraft in kilonewtons (kN)
     *
     * @param aircraftId - Aircraft identifier
     * @returns Total design load in kN
     *
     * @example
     * ```typescript
     * const load = AircraftConfigurationService.getDesignTotalLoad(10);
     * // Returns: 6400 (kN) for Antonov An-225 Mriya
     * ```
     */
    static getDesignTotalLoad(aircraftId: number): number {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        return config?.designTotalLoad ?? 0;
    }

    /**
     * Get aircraft name (short version)
     *
     * @param aircraftId - Aircraft identifier
     * @returns Aircraft short name or undefined
     */
    static getName(aircraftId: number): string | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        return config?.name;
    }

    /**
     * Get aircraft full name
     *
     * @param aircraftId - Aircraft identifier
     * @returns Aircraft full name or undefined
     */
    static getFullName(aircraftId: number): string | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        return config?.fullName;
    }

    /**
     * Get aircraft image path
     *
     * @param aircraftId - Aircraft identifier
     * @returns Relative path to aircraft image
     */
    static getImagePath(aircraftId: number): string | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        return config?.imagePath;
    }

    /**
     * Get Excel cell references for traceability
     *
     * @param aircraftId - Aircraft identifier
     * @returns Excel cell/range references object or undefined
     */
    static getExcelReferences(aircraftId: number): AircraftConfiguration['excelReferences'] | undefined {
        const config = AIRCRAFT_CONFIGURATIONS.get(aircraftId);
        return config?.excelReferences;
    }

    /**
     * Get all available aircraft IDs
     * Useful for validation and UI display
     *
     * @returns Array of available aircraft IDs (1-10)
     *
     * @example
     * ```typescript
     * const ids = AircraftConfigurationService.getAvailableAircraftIds();
     * // Returns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
     * ```
     */
    static getAvailableAircraftIds(): number[] {
        return Array.from(AIRCRAFT_CONFIGURATIONS.keys()).sort((a, b) => a - b);
    }

    /**
     * Get all aircraft configurations
     * Useful for debugging and validation
     *
     * @returns Map of aircraft ID → configuration
     */
    static getAllConfigurations(): Map<number, AircraftConfiguration> {
        return new Map(AIRCRAFT_CONFIGURATIONS);
    }

    /**
     * Validate if an aircraft ID is explicitly defined in the table
     *
     * @param aircraftId - Aircraft ID to check
     * @returns true if aircraft ID is in table, false otherwise
     *
     * @example
     * ```typescript
     * AircraftConfigurationService.isAircraftDefined(5); // true (BOEING 767-400 ER)
     * AircraftConfigurationService.isAircraftDefined(99); // false
     * ```
     */
    static isAircraftDefined(aircraftId: number): boolean {
        return AIRCRAFT_CONFIGURATIONS.has(aircraftId);
    }
}
